import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import { clearSignedIn, setUnauthorizedHandler } from "./lib/api";
import { ModeProvider } from "./lib/mode";
import { ME_KEY } from "./lib/queries";
import { router } from "./router";
import "./index.css";
import { ApiError } from "@unizpay/ui";

/**
 * A 403 (role lacks the permission) or 404 (no longer a member) can mean the user's role or
 * memberships changed since /me was loaded: reload it so the UI shows what they can do now.
 */
function refreshAccess(err: unknown) {
  if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
  }
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: refreshAccess }),
  mutationCache: new MutationCache({ onError: refreshAccess }),
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      // Client errors (4xx) won't succeed on retry; network/5xx errors get two more tries.
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

// Any 401 from /dashboard-api means the session is gone: drop cached data and go to sign-in.
// The invite page handles signed-out visitors itself.
setUnauthorizedHandler(() => {
  clearSignedIn();
  const path = window.location.pathname;
  if (path.startsWith("/login") || path.startsWith("/invite/") || path.startsWith("/payout-change/")) return;
  queryClient.clear();
  void router.navigate("/login", { replace: true });
});

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root element");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ModeProvider>
        <RouterProvider router={router} />
      </ModeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
